import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Eye, EyeOff, Archive } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AssistantModal from './AssistantModal';

interface Assistant {
  id: string;
  firstname: string;
  lastname: string;
  archived: boolean;
  agencies: {
    name: string;
  }[];
}

export default function AssistantSettings() {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [filteredAssistants, setFilteredAssistants] = useState<Assistant[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterAndSortAssistants();
  }, [searchTerm, assistants, showArchived]);

  const loadData = async () => {
    try {
      const { data: assistantsData, error: assistantsError } = await supabase
        .from('assistants')
        .select(`
          id,
          firstname,
          lastname,
          archived,
          assistant_agencies (
            agency
          )
        `)
        .order('lastname, firstname');

      if (assistantsError) throw assistantsError;

      const formattedData = (assistantsData || []).map(a => ({
        id: a.id,
        firstname: a.firstname,
        lastname: a.lastname,
        archived: a.archived || false,
        agencies: (a.assistant_agencies || []).map(ag => ({
          name: ag.agency
        }))
      }));

      setAssistants(formattedData);
      setFilteredAssistants(formattedData);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const filterAndSortAssistants = () => {
    const filtered = assistants.filter(assistant => {
      const fullName = `${assistant.firstname} ${assistant.lastname}`.toLowerCase();
      const agencies = assistant.agencies.map(a => a.name.toLowerCase());
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = fullName.includes(searchLower) ||
                            agencies.some(agency => agency.includes(searchLower));
      const matchesArchived = showArchived ? assistant.archived : !assistant.archived;
      return matchesSearch && matchesArchived;
    });
    setFilteredAssistants(filtered);
  };

  const handleToggleArchive = async (assistant: Assistant) => {
    try {
      const { error } = await supabase
        .from('assistants')
        .update({ archived: !assistant.archived })
        .eq('id', assistant.id);

      if (error) throw error;
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEditClick = (assistant: Assistant) => {
    setSelectedAssistant(assistant);
    setIsModalOpen(true);
  };

  const handleAddClick = () => {
    setSelectedAssistant(null);
    setIsModalOpen(true);
  };

  const handleDeleteAssistant = async (assistant: Assistant) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer ${assistant.firstname} ${assistant.lastname} ?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('assistants')
        .delete()
        .eq('id', assistant.id);

      if (error) throw error;
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher une assistante ou une agence…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
          />
        </div>
        <button
          onClick={() => setShowArchived(!showArchived)}
          className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border transition-colors ${
            showArchived
              ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          <Archive size={15} />
          <span>{showArchived ? 'Voir les actives' : 'Voir les masquées'}</span>
        </button>
        <button
          onClick={handleAddClick}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 text-sm font-semibold transition-colors shadow-sm"
        >
          <Plus size={16} />
          <span>Nouvelle assistante</span>
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">{error}</div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-100">
          <span className="text-sm font-semibold text-slate-700">Assistantes</span>
          <span className="text-xs text-slate-400">{filteredAssistants.length} résultat{filteredAssistants.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Nom</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Prénom</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Agences</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {filteredAssistants.map((assistant) => (
                <tr
                  key={assistant.id}
                  className={`border-b border-slate-100 last:border-0 transition-colors ${
                    assistant.archived ? 'bg-slate-50/60 hover:bg-slate-100/60' : 'hover:bg-indigo-50/30'
                  }`}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-slate-800">{assistant.lastname}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">{assistant.firstname}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    <div className="flex flex-wrap gap-1">
                      {assistant.agencies.map((agency) => (
                        <span key={agency.name} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{agency.name}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleEditClick(assistant)}
                        className="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-colors"
                        title="Modifier l'assistante"
                      >
                        <Edit size={15} />
                      </button>
                      <button
                        onClick={() => handleToggleArchive(assistant)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          assistant.archived
                            ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-600'
                            : 'bg-orange-50 hover:bg-orange-100 text-orange-600'
                        }`}
                        title={assistant.archived ? 'Afficher dans les listes' : 'Masquer des listes'}
                      >
                        {assistant.archived ? <Eye size={15} /> : <EyeOff size={15} />}
                      </button>
                      <button
                        onClick={() => handleDeleteAssistant(assistant)}
                        className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                        title="Supprimer l'assistante"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AssistantModal
        assistant={selectedAssistant}
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedAssistant(null); }}
        onSuccess={loadData}
      />
    </div>
  );
}